import type { FloorTheme } from './themes';

type ThemeLike = Pick<FloorTheme, 'colors'>;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const parseHex = (color: string) => {
    const normalized = color.trim().replace('#', '');
    if (normalized.length !== 3 && normalized.length !== 6) return null;
    const full = normalized.length === 3
        ? normalized.split('').map((char) => char + char).join('')
        : normalized;
    const r = Number.parseInt(full.slice(0, 2), 16);
    const g = Number.parseInt(full.slice(2, 4), 16);
    const b = Number.parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some((value) => Number.isNaN(value))) return null;
    return { r, g, b };
};

const parseRgb = (color: string) => {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!match) return null;
    return {
        r: Number(match[1]),
        g: Number(match[2]),
        b: Number(match[3]),
    };
};

const toRgb = (color: string) => parseHex(color) ?? parseRgb(color);

const channelToLinear = (value: number) => {
    const normalized = value / 255;
    return normalized <= 0.03928
        ? normalized / 12.92
        : ((normalized + 0.055) / 1.055) ** 2.4;
};

export const getLuminance = (color: string) => {
    const rgb = toRgb(color);
    if (!rgb) return 0;
    return (
        0.2126 * channelToLinear(rgb.r) +
        0.7152 * channelToLinear(rgb.g) +
        0.0722 * channelToLinear(rgb.b)
    );
};

export const withAlpha = (color: string, alpha: number) => {
    const rgb = toRgb(color);
    if (!rgb) return color;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${clamp(alpha, 0, 1)})`;
};

export const pickTextColor = (
    background: string,
    dark = '#111111',
    light = '#ffffff'
) => (getLuminance(background) > 0.42 ? dark : light);

export const isDarkColor = (color: string) => getLuminance(color) < 0.22;

export const isDarkTheme = (theme: ThemeLike) => isDarkColor(theme.colors.background || theme.colors.card);

export const getMutedTextColor = (theme: ThemeLike) =>
    withAlpha(theme.colors.text, isDarkTheme(theme) ? 0.74 : 0.68);

export const getPanelStyle = (
    theme: ThemeLike,
    darkAlpha = 0.94,
    lightAlpha = 0.88
) => ({
    backgroundColor: withAlpha(theme.colors.card, isDarkTheme(theme) ? darkAlpha : lightAlpha),
    borderColor: theme.colors.border,
    color: theme.colors.text,
});

export const getSoftSurface = (
    theme: ThemeLike,
    darkAlpha = 0.82,
    lightAlpha = 0.58
) => withAlpha(theme.colors.secondary, isDarkTheme(theme) ? darkAlpha : lightAlpha);

export const getInputSurface = (theme: ThemeLike) =>
    isDarkTheme(theme) ? withAlpha(theme.colors.background, 0.78) : '#ffffff';
